import prettyBytes from 'pretty-bytes';
import type { DetectionResult, UploadConfig } from '../types';

// Compound extensions to recognise as one piece (matches the backend
// validation). Keep in sync with apps/backend/src/services/uploads.validation.ts.
const COMPOUND_EXTENSIONS = ['.tar.gz'];

const RECORDING_VIDEO_MIMES = new Set(['video/mp4', 'video/webm']);

export function extractExtension(filename: string): string {
  if (!filename) return '';
  const base = (filename.split('/').pop() ?? '').toLowerCase();
  // Dotfile (`.env`, `.gitignore`) — no extension by convention.
  if (base.startsWith('.') && !base.slice(1).includes('.')) return '';

  for (const compound of COMPOUND_EXTENSIONS) {
    if (base.endsWith(compound) && base.length > compound.length) {
      return compound;
    }
  }

  const dot = base.lastIndexOf('.');
  if (dot === -1) return '';
  return base.slice(dot);
}

function looksLikeRecording(mime: string): boolean {
  if (!mime) return false;
  if (mime.startsWith('audio/')) return true;
  return RECORDING_VIDEO_MIMES.has(mime);
}

function looksLikeSyslog(file: File, config: UploadConfig): boolean {
  // Either MIME or extension matching is enough — files coming from Linux
  // tooling commonly arrive with `application/octet-stream` and the real signal
  // is the extension.
  if (config.syslog.mimeTypes.includes(file.type)) return true;
  const ext = extractExtension(file.name);
  if (!ext) return false;
  return config.syslog.extensions.includes(ext);
}

// Pure function: routes a file to the right upload code path AND tells the
// caller whether to disable the submit button. The backend re-validates
// regardless — this is purely for UX (right size guidance, right endpoint,
// right Content-Type on the presigned PUT).
export function detectType(file: File, config: UploadConfig): DetectionResult {
  const isRecording = looksLikeRecording(file.type);
  const isSyslog = looksLikeSyslog(file, config);

  if (isRecording && isSyslog) {
    return {
      type: 'ambiguous',
      reason: 'File matches both recording and syslog rules — pick a type to continue',
    };
  }

  if (isRecording) {
    if (file.size > config.recording.maxBytes) {
      return {
        type: 'rejected',
        reason: `File too large for recording (max ${prettyBytes(config.recording.maxBytes)})`,
      };
    }
    return { type: 'recording' };
  }

  if (isSyslog) {
    if (file.size > config.syslog.maxBytes) {
      return {
        type: 'rejected',
        reason: `File too large for syslog (max ${prettyBytes(config.syslog.maxBytes)})`,
      };
    }
    return { type: 'syslog' };
  }

  return {
    type: 'rejected',
    reason: 'Unsupported file type',
  };
}
